import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthenticatedFetch } from '../utils/api';
import { showSuccessToast, showErrorToast } from '../utils/toast';

function SettingsPage() {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [optimizationTypes, setOptimizationTypes] = useState([]);
  const [defaultType, setDefaultType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingTypeId, setSavingTypeId] = useState(null);
  const [error, setError] = useState(null);
  const authenticatedFetchRef = useRef(authenticatedFetch);

  useEffect(() => {
    authenticatedFetchRef.current = authenticatedFetch;
  }, [authenticatedFetch]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const typesResponse = await authenticatedFetchRef.current('/api/optimization-types');
        if (!isMounted) return;
        if (!typesResponse.ok) throw new Error('Failed to fetch optimization types');
        const typesData = await typesResponse.json();
        setOptimizationTypes(typesData.types || []);

        const defaultResponse = await authenticatedFetchRef.current('/api/optimization-types/default');
        if (!isMounted) return;
        if (defaultResponse.ok) {
          const defaultData = await defaultResponse.json();
          setDefaultType(defaultData.type || null);
        } else if (defaultResponse.status === 404) {
          setDefaultType(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load settings');
          showErrorToast(err.message || 'Failed to load settings');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const handleDefaultTypeChange = useCallback(async (typeId) => {
    try {
      setSavingTypeId(typeId);
      setError(null);
      const response = await authenticatedFetchRef.current('/api/settings/default-optimization-type', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optimizationTypeId: typeId }),
      });
      if (!response.ok) throw new Error('Failed to update default optimization type');
      const data = await response.json();
      setDefaultType(data.type || null);
      showSuccessToast('Default optimization type updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update default settings');
      showErrorToast(err.message || 'Failed to update settings');
    } finally {
      setSavingTypeId(null);
    }
  }, []);

  if (loading) {
    return (
      <s-section>
        <s-stack gap="base">
          <s-heading>Settings</s-heading>
          <s-text color="subdued">Choose your default image optimization level.</s-text>
          <div className="shimmer-loader" style={{ height: 200, width: '100%' }} />
        </s-stack>
      </s-section>
    );
  }

  return (
    <s-section>
      <s-stack gap="base">
        <s-heading>Settings</s-heading>
        {error && <s-banner tone="critical"><s-text>{error}</s-text></s-banner>}

        <s-box padding="base" background="base" border="base" borderRadius="base">
          <s-stack gap="base">
            <s-heading size="small">Default Optimization Type</s-heading>
            <s-text color="subdued">
              Select the default optimization type that will be used for new optimization jobs.
            </s-text>

            {optimizationTypes.length === 0 ? (
              <s-banner><s-text>No optimization types available.</s-text></s-banner>
            ) : (
              <s-stack gap="base">
                {optimizationTypes.map((type) => (
                  <s-box
                    key={type.id}
                    padding="base"
                    background={defaultType?.id === type.id ? 'subdued' : 'base'}
                    border="base"
                    borderRadius="base"
                  >
                    <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
                      <s-stack gap="small">
                        <s-stack direction="inline" gap="small" alignItems="center">
                          <s-heading size="small">{type.title}</s-heading>
                          {defaultType?.id === type.id && <s-badge tone="success">Default</s-badge>}
                        </s-stack>
                        <s-text color="subdued">{type.description || 'No description'}</s-text>
                        <s-text color="subdued" size="small">
                          Quality: {type.quality}% | Max Width: {type.maxWidth}px
                        </s-text>
                      </s-stack>
                      <s-button
                        variant={defaultType?.id === type.id ? 'primary' : 'secondary'}
                        onClick={() => handleDefaultTypeChange(type.id)}
                        disabled={savingTypeId !== null || defaultType?.id === type.id}
                        loading={savingTypeId === type.id}
                      >
                        {defaultType?.id === type.id ? 'Current Default' : 'Set as Default'}
                      </s-button>
                    </s-stack>
                  </s-box>
                ))}
              </s-stack>
            )}
          </s-stack>
        </s-box>
      </s-stack>
    </s-section>
  );
}

export default SettingsPage;
